export type Language = 'en' | 'zh' | 'id'

export type Translations = {
  navbar: {
    home: string
    about: string
    products: string
    quality: string
    contact: string
    requestQuote: string
  }
  hero: {
    tagline: string
    headline: string
    supporting: string
    requestQuote: string
    contactUs: string
    trustBadges: {
      exportStandards: string
      traceablePackaging: string
      ethicalSourcing: string
    }
    companyName: string
    established: string
  }
  executiveSummary: {
    badge: string
    title: string
    paragraph1: string
    paragraph2: string
    keyPoints: string[]
    commitment: {
      title: string
      description: string
      highlights: {
        quality: string
        standards: string
        practices: string
      }
    }
  }
  companyBackground: {
    badge: string
    title: string
    subtitle: string
    objectives: {
      enhance: { title: string; description: string }
      bridge: { title: string; description: string }
      promote: { title: string; description: string }
    }
    gcg: {
      label: string
      text: string
    }
  }
  visionMission: {
    badge: string
    title: string
    vision: {
      title: string
      text: string
    }
    mission: {
      title: string
      points: string[]
    }
  }
  values: {
    badge: string
    title: string
    subtitle: string
    items: {
      sustainability: { title: string; description: string }
      compliance: { title: string; description: string }
      quality: { title: string; description: string }
      community: { title: string; description: string }
    }
  }
  products: {
    badge: string
    title: string
    subtitle: string
    items: {
      driedLeaves: {
        name: string
        description: string
        features: string[]
      }
      powder: {
        name: string
        description: string
        features: string[]
      }
      crushedLeaf: {
        name: string
        description: string
        features: string[]
      }
    }
    processing: {
      title: string
      description: string
      steps: string[]
      badge: string
    }
  }
  qualityStandards: {
    badge: string
    title: string
    subtitle: string
    standards: {
      sorting: { title: string; description: string }
      hygiene: { title: string; description: string }
      packaging: { title: string; description: string }
      compliance: { title: string; description: string }
    }
    statement: string
  }
  legalCompliance: {
    badge: string
    title: string
    subtitle: string
    documents: {
      businessLicense: { title: string; status: string }
      exportDoc: { title: string; status: string }
      qualityDoc: { title: string; status: string }
      traceability: { title: string; status: string }
    }
    note: {
      title: string
      text: string
    }
  }
  partnership: {
    badge: string
    title: string
    subtitle: string
    partners: {
      title: string
      items: string[]
    }
    sustainability: {
      title: string
      items: string[]
    }
  }
  market: {
    badge: string
    title: string
    subtitle: string
    targetMarkets: {
      title: string
      items: string[]
    }
    primaryMarkets: {
      title: string
      domestic: { label: string; description: string }
      international: { label: string; description: string }
      serving: string
    }
  }
  contact: {
    badge: string
    title: string
    subtitle: string
    info: {
      phone: string
      email: string
      website: string
      location: string
    }
    since: string
    footer: {
      tagline: string
      copyright: string
    }
  }
  quoteModal: {
    title: string
    subtitle: string
    fields: {
      name: { label: string; placeholder: string }
      email: { label: string; placeholder: string }
      company: { label: string; placeholder: string }
      message: { label: string; placeholder: string }
    }
    submit: string
    submitting: string
    success: {
      title: string
      message: string
    }
  }
}

export const translations: Record<Language, Translations> = {
  en: {
    navbar: {
      home: 'Home',
      about: 'About',
      products: 'Products',
      quality: 'Quality',
      contact: 'Contact',
      requestQuote: 'Request Quote',
    },
    hero: {
      tagline: 'Quality • Compliance • Sustainability',
      headline: 'Premium Indonesian Kratom for Global Markets',
      supporting:
        'Trading, processing, and distribution of kratom (Mitragyna speciosa) with a focus on legality, sustainability, and compliance with international standards.',
      requestQuote: 'Request Quote',
      contactUs: 'Contact Us',
      trustBadges: {
        exportStandards: 'Export Standards',
        traceablePackaging: 'Traceable Packaging',
        ethicalSourcing: 'Ethical Sourcing',
      },
      companyName: 'PT Kratom Borneo Indonesia',
      established: 'Established 2025',
    },
    executiveSummary: {
      badge: 'About Us',
      title: 'Executive Summary',
      paragraph1:
        'KBIN is a professional company engaged in trading, processing, and distribution of kratom. We are committed to delivering high-quality products while prioritizing legality, sustainability, and compliance with national and international regulations.',
      paragraph2:
        'We aim to be a reliable partner for local farmers, distributors, and global buyers through transparent and ethical practices. Our operations are built on a foundation of trust, quality assurance, and sustainable business practices.',
      keyPoints: [
        'Professional trading and processing operations',
        'Commitment to legality and compliance',
        'Sustainable and ethical business practices',
        'Reliable partner for global markets',
      ],
      commitment: {
        title: 'Our Commitment',
        description:
          'We bridge the gap between Indonesian kratom farmers and global markets, ensuring quality, compliance, and fair trade at every step of the supply chain.',
        highlights: {
          quality: 'High-Quality Products',
          standards: 'International Standards',
          practices: 'Ethical Practices',
        },
      },
    },
    companyBackground: {
      badge: 'Our Foundation',
      title: 'Company Background',
      subtitle: 'Built on principles of integrity, quality, and sustainability',
      objectives: {
        enhance: {
          title: 'Enhance Value',
          description:
            'Enhance the value of Indonesian kratom products through quality processing and professional standards',
        },
        bridge: {
          title: 'Bridge Markets',
          description:
            'Bridge local farmers with global markets, creating sustainable economic opportunities',
        },
        promote: {
          title: 'Promote Legal Trade',
          description:
            'Promote legal, safe, and sustainable kratom trade in compliance with regulations',
        },
      },
      gcg: {
        label: 'Good Corporate Governance',
        text: 'We operate under Good Corporate Governance (GCG) principles and maintain ethical business conduct in all our operations.',
      },
    },
    visionMission: {
      badge: 'Our Direction',
      title: 'Vision & Mission',
      vision: {
        title: 'Our Vision',
        text: 'To become a trusted, globally competitive, and sustainable Indonesian kratom company that sets the standard for quality and compliance in the industry.',
      },
      mission: {
        title: 'Our Mission',
        points: [
          'Supply high-quality export-standard products',
          'Build strategic partnerships with local farmers',
          'Ensure full compliance with government regulations and international laws',
          'Uphold transparency, professionalism, and environmental sustainability',
        ],
      },
    },
    values: {
      badge: 'What We Stand For',
      title: 'Our Values',
      subtitle: 'The principles that guide every decision we make',
      items: {
        sustainability: {
          title: 'Sustainability',
          description:
            'Committed to environmentally responsible practices and sustainable harvesting methods that protect our natural resources.',
        },
        compliance: {
          title: 'Compliance',
          description:
            'Adhering to all national and international regulations, ensuring legal and transparent operations at every level.',
        },
        quality: {
          title: 'Quality',
          description:
            'Maintaining the highest standards in processing, packaging, and delivery to ensure premium product quality.',
        },
        community: {
          title: 'Community',
          description:
            'Supporting local farmers and communities through fair partnerships and economic empowerment initiatives.',
        },
      },
    },
    products: {
      badge: 'What We Offer',
      title: 'Our Products',
      subtitle: 'Premium kratom products processed to the highest standards',
      items: {
        driedLeaves: {
          name: 'Dried Kratom Leaves',
          description:
            'Premium whole dried leaves, carefully harvested and processed to preserve natural properties.',
          features: [
            'Hand-selected leaves',
            'Natural drying process',
            'Quality graded',
          ],
        },
        powder: {
          name: 'Kratom Powder',
          description:
            'Finely ground kratom powder produced under strict hygiene standards for consistent quality.',
          features: ['Fine mesh grinding', 'Consistent texture', 'Lab tested'],
        },
        crushedLeaf: {
          name: 'Crushed Leaf Kratom',
          description:
            'Coarsely processed kratom leaves, ideal for various applications and preparations.',
          features: ['Uniform crushing', 'Preserved potency', 'Bulk available'],
        },
      },
      processing: {
        title: 'Processing & Quality Control',
        description:
          'Our rigorous processing standards ensure every product meets export-grade quality requirements.',
        steps: [
          'Raw material selection and grading',
          'Controlled drying process',
          'Hygienic grinding and processing',
          'Internal quality control and testing',
        ],
        badge: 'Export-Grade Quality',
      },
    },
    qualityStandards: {
      badge: 'Our Standards',
      title: 'Quality Standards & Regulatory Compliance',
      subtitle:
        'Meeting and exceeding international quality and compliance requirements',
      standards: {
        sorting: {
          title: 'Raw Material Sorting',
          description:
            'Rigorous sorting and grading of raw materials to ensure only premium quality enters our processing line.',
        },
        hygiene: {
          title: 'Hygiene Control',
          description:
            'Strict hygiene and sanitation protocols maintained throughout all processing and packaging stages.',
        },
        packaging: {
          title: 'Traceable Packaging',
          description:
            'Secure and traceable packaging systems that ensure product integrity from facility to destination.',
        },
        compliance: {
          title: 'Export Compliance',
          description:
            'Full compliance with export regulations and documentation requirements for international trade.',
        },
      },
      statement:
        'We adhere to applicable government policies and regulations regarding kratom trade and export activities, ensuring full legal compliance in all our operations.',
    },
    legalCompliance: {
      badge: 'Documentation',
      title: 'Legal Compliance',
      subtitle: 'Permits & Documentation',
      documents: {
        businessLicense: {
          title: 'Business License',
          status: 'Available upon request',
        },
        exportDoc: {
          title: 'Export Documentation',
          status: 'Available upon request',
        },
        qualityDoc: {
          title: 'Quality Documentation',
          status: 'Available upon request',
        },
        traceability: {
          title: 'Traceability Records',
          status: 'Available upon request',
        },
      },
      note: {
        title: 'Documentation Request',
        text: 'All documentation is available upon request for qualified partners. Please contact us to request access to our compliance documentation and certifications.',
      },
    },
    partnership: {
      badge: 'Working Together',
      title: 'Partnership & Sustainability',
      subtitle:
        'Building lasting relationships while protecting our environment',
      partners: {
        title: 'We collaborate directly with:',
        items: [
          'Local kratom farmers',
          'Farmer groups and cooperatives',
          'Logistics and export partners',
        ],
      },
      sustainability: {
        title: 'Our sustainability principles include:',
        items: [
          'Environmentally responsible harvesting',
          'Farmer education and guidance',
          'Fair pricing mechanisms',
          'Local economic empowerment',
        ],
      },
    },
    market: {
      badge: 'Global Reach',
      title: 'Market & Segmentation',
      subtitle: 'Serving diverse markets with premium Indonesian kratom',
      targetMarkets: {
        title: 'Target Markets',
        items: [
          'Distributors and wholesalers',
          'International importers',
          'Herbal, research, and industrial sectors',
        ],
      },
      primaryMarkets: {
        title: 'Primary Markets',
        domestic: {
          label: 'Domestic',
          description: 'Indonesian market distribution',
        },
        international: {
          label: 'International',
          description: 'Global export operations',
        },
        serving: 'Serving customers worldwide from Indonesia',
      },
    },
    contact: {
      badge: 'Get In Touch',
      title: 'Contact Us',
      subtitle: 'Ready to discuss your kratom sourcing needs? Reach out to us.',
      info: {
        phone: 'Phone Number',
        email: 'Email Address',
        website: 'Website',
        location: 'Location',
      },
      since: 'Since 2025',
      footer: {
        tagline: 'Quality • Compliance • Sustainability',
        copyright: 'PT Kratom Borneo Indonesia. All rights reserved.',
      },
    },
    quoteModal: {
      title: 'Request a Quote',
      subtitle:
        "Fill out the form below and we'll get back to you within 24 hours.",
      fields: {
        name: {
          label: 'Full Name',
          placeholder: 'John Doe',
        },
        email: {
          label: 'Email Address',
          placeholder: 'john@company.com',
        },
        company: {
          label: 'Company Name',
          placeholder: 'Your Company Ltd.',
        },
        message: {
          label: 'Message / Requirements',
          placeholder:
            'Tell us about your requirements, quantities, and any specific needs...',
        },
      },
      submit: 'Send Request',
      submitting: 'Sending...',
      success: {
        title: 'Request Sent!',
        message: "Thank you for your interest. We'll be in touch soon.",
      },
    },
  },
  zh: {
    navbar: {
      home: '首页',
      about: '关于我们',
      products: '产品',
      quality: '质量',
      contact: '联系我们',
      requestQuote: '索取报价',
    },
    hero: {
      tagline: '质量 • 合规 • 可持续',
      headline: '优质印尼卡痛叶 服务全球市场',
      supporting:
        '专注于卡痛叶（Mitragyna speciosa）的贸易、加工和分销，注重合法性、可持续性和国际标准合规性。',
      requestQuote: '索取报价',
      contactUs: '联系我们',
      trustBadges: {
        exportStandards: '出口标准',
        traceablePackaging: '可追溯包装',
        ethicalSourcing: '道德采购',
      },
      companyName: '印尼婆罗洲卡痛叶公司',
      established: '成立于2025年',
    },
    executiveSummary: {
      badge: '关于我们',
      title: '公司简介',
      paragraph1:
        'KBIN是一家专业从事卡痛叶贸易、加工和分销的公司。我们致力于提供高质量的产品，同时优先考虑合法性、可持续性以及符合国家和国际法规。',
      paragraph2:
        '我们旨在通过透明和道德的实践，成为当地农民、分销商和全球买家的可靠合作伙伴。我们的运营建立在信任、质量保证和可持续商业实践的基础上。',
      keyPoints: [
        '专业的贸易和加工运营',
        '致力于合法性和合规性',
        '可持续和道德的商业实践',
        '全球市场的可靠合作伙伴',
      ],
      commitment: {
        title: '我们的承诺',
        description:
          '我们在印尼卡痛叶农民和全球市场之间架起桥梁，确保供应链每一步的质量、合规性和公平贸易。',
        highlights: {
          quality: '高质量产品',
          standards: '国际标准',
          practices: '道德实践',
        },
      },
    },
    companyBackground: {
      badge: '我们的基础',
      title: '公司背景',
      subtitle: '建立在诚信、质量和可持续性原则之上',
      objectives: {
        enhance: {
          title: '提升价值',
          description: '通过质量加工和专业标准提升印尼卡痛叶产品的价值',
        },
        bridge: {
          title: '连接市场',
          description: '连接当地农民与全球市场，创造可持续的经济机会',
        },
        promote: {
          title: '促进合法贸易',
          description: '促进符合法规的合法、安全和可持续的卡痛叶贸易',
        },
      },
      gcg: {
        label: '良好企业治理',
        text: '我们按照良好企业治理（GCG）原则运营，并在所有运营中保持道德商业行为。',
      },
    },
    visionMission: {
      badge: '我们的方向',
      title: '愿景与使命',
      vision: {
        title: '我们的愿景',
        text: '成为值得信赖、具有全球竞争力和可持续发展的印尼卡痛叶公司，为行业树立质量和合规标准。',
      },
      mission: {
        title: '我们的使命',
        points: [
          '供应符合出口标准的高质量产品',
          '与当地农民建立战略合作伙伴关系',
          '确保完全符合政府法规和国际法律',
          '坚持透明度、专业性和环境可持续性',
        ],
      },
    },
    values: {
      badge: '我们的立场',
      title: '我们的价值观',
      subtitle: '指导我们每一个决策的原则',
      items: {
        sustainability: {
          title: '可持续性',
          description: '致力于环保实践和可持续采收方法，保护我们的自然资源。',
        },
        compliance: {
          title: '合规性',
          description: '遵守所有国家和国际法规，确保各级合法透明的运营。',
        },
        quality: {
          title: '质量',
          description: '在加工、包装和交付方面保持最高标准，确保优质产品质量。',
        },
        community: {
          title: '社区',
          description: '通过公平合作和经济赋权计划支持当地农民和社区。',
        },
      },
    },
    products: {
      badge: '我们的产品',
      title: '产品系列',
      subtitle: '按最高标准加工的优质卡痛叶产品',
      items: {
        driedLeaves: {
          name: '干燥卡痛叶',
          description: '优质完整干叶，经过精心采收和加工以保留天然特性。',
          features: ['手工挑选叶片', '自然干燥过程', '质量分级'],
        },
        powder: {
          name: '卡痛叶粉',
          description: '在严格卫生标准下生产的细磨卡痛叶粉，质量稳定。',
          features: ['细网研磨', '质地均匀', '实验室测试'],
        },
        crushedLeaf: {
          name: '碎叶卡痛',
          description: '粗加工的卡痛叶，适用于各种应用和制备。',
          features: ['均匀破碎', '保留效力', '可批量供应'],
        },
      },
      processing: {
        title: '加工与质量控制',
        description: '我们严格的加工标准确保每个产品都符合出口级质量要求。',
        steps: [
          '原材料选择和分级',
          '受控干燥过程',
          '卫生研磨和加工',
          '内部质量控制和测试',
        ],
        badge: '出口级质量',
      },
    },
    qualityStandards: {
      badge: '我们的标准',
      title: '质量标准与法规合规',
      subtitle: '满足并超越国际质量和合规要求',
      standards: {
        sorting: {
          title: '原材料分拣',
          description:
            '严格分拣和分级原材料，确保只有优质材料进入我们的加工线。',
        },
        hygiene: {
          title: '卫生控制',
          description: '在所有加工和包装阶段保持严格的卫生和消毒协议。',
        },
        packaging: {
          title: '可追溯包装',
          description: '安全可追溯的包装系统，确保产品从设施到目的地的完整性。',
        },
        compliance: {
          title: '出口合规',
          description: '完全符合国际贸易的出口法规和文件要求。',
        },
      },
      statement:
        '我们遵守有关卡痛叶贸易和出口活动的适用政府政策和法规，确保所有运营的完全合法合规。',
    },
    legalCompliance: {
      badge: '文件',
      title: '法律合规',
      subtitle: '许可证与文件',
      documents: {
        businessLicense: {
          title: '营业执照',
          status: '应要求提供',
        },
        exportDoc: {
          title: '出口文件',
          status: '应要求提供',
        },
        qualityDoc: {
          title: '质量文件',
          status: '应要求提供',
        },
        traceability: {
          title: '可追溯性记录',
          status: '应要求提供',
        },
      },
      note: {
        title: '文件申请',
        text: '所有文件均可应合格合作伙伴的要求提供。请联系我们申请访问我们的合规文件和认证。',
      },
    },
    partnership: {
      badge: '共同合作',
      title: '合作与可持续性',
      subtitle: '在保护环境的同时建立持久关系',
      partners: {
        title: '我们直接合作对象：',
        items: ['当地卡痛叶农民', '农民团体和合作社', '物流和出口合作伙伴'],
      },
      sustainability: {
        title: '我们的可持续性原则包括：',
        items: ['环保采收', '农民教育和指导', '公平定价机制', '当地经济赋权'],
      },
    },
    market: {
      badge: '全球覆盖',
      title: '市场与细分',
      subtitle: '以优质印尼卡痛叶服务多元化市场',
      targetMarkets: {
        title: '目标市场',
        items: ['分销商和批发商', '国际进口商', '草药、研究和工业领域'],
      },
      primaryMarkets: {
        title: '主要市场',
        domestic: {
          label: '国内',
          description: '印尼市场分销',
        },
        international: {
          label: '国际',
          description: '全球出口业务',
        },
        serving: '从印尼为全球客户提供服务',
      },
    },
    contact: {
      badge: '联系我们',
      title: '联系方式',
      subtitle: '准备讨论您的卡痛叶采购需求？请联系我们。',
      info: {
        phone: '电话号码',
        email: '电子邮件',
        website: '网站',
        location: '地址',
      },
      since: '成立于2025年',
      footer: {
        tagline: '质量 • 合规 • 可持续',
        copyright: '印尼婆罗洲卡痛叶公司。保留所有权利。',
      },
    },
    quoteModal: {
      title: '索取报价',
      subtitle: '填写下面的表格，我们将在24小时内回复您。',
      fields: {
        name: {
          label: '全名',
          placeholder: '张三',
        },
        email: {
          label: '电子邮件地址',
          placeholder: 'zhang@company.com',
        },
        company: {
          label: '公司名称',
          placeholder: '您的公司有限公司',
        },
        message: {
          label: '留言/需求',
          placeholder: '告诉我们您的要求、数量和任何特殊需求...',
        },
      },
      submit: '发送请求',
      submitting: '发送中...',
      success: {
        title: '请求已发送！',
        message: '感谢您的关注。我们很快会与您联系。',
      },
    },
  },
  id: {
    navbar: {
      home: 'Beranda',
      about: 'Tentang',
      products: 'Produk',
      quality: 'Kualitas',
      contact: 'Kontak',
      requestQuote: 'Minta Penawaran',
    },
    hero: {
      tagline: 'Kualitas • Kepatuhan • Keberlanjutan',
      headline: 'Kratom Premium Indonesia untuk Pasar Global',
      supporting:
        'Perdagangan, pengolahan, dan distribusi kratom (Mitragyna speciosa) dengan fokus pada legalitas, keberlanjutan, dan kepatuhan terhadap standar internasional.',
      requestQuote: 'Minta Penawaran',
      contactUs: 'Hubungi Kami',
      trustBadges: {
        exportStandards: 'Standar Ekspor',
        traceablePackaging: 'Kemasan Tertelusur',
        ethicalSourcing: 'Sumber Etis',
      },
      companyName: 'PT Kratom Borneo Indonesia',
      established: 'Didirikan 2025',
    },
    executiveSummary: {
      badge: 'Tentang Kami',
      title: 'Ringkasan Eksekutif',
      paragraph1:
        'KBIN adalah perusahaan profesional yang bergerak di bidang perdagangan, pengolahan, dan distribusi kratom. Kami berkomitmen untuk menyediakan produk berkualitas tinggi dengan mengutamakan legalitas, keberlanjutan, dan kepatuhan terhadap peraturan nasional dan internasional.',
      paragraph2:
        'Kami bertujuan menjadi mitra yang dapat diandalkan bagi petani lokal, distributor, dan pembeli global melalui praktik yang transparan dan etis. Operasi kami dibangun atas dasar kepercayaan, jaminan kualitas, dan praktik bisnis yang berkelanjutan.',
      keyPoints: [
        'Operasi perdagangan dan pengolahan profesional',
        'Komitmen terhadap legalitas dan kepatuhan',
        'Praktik bisnis yang berkelanjutan dan etis',
        'Mitra terpercaya untuk pasar global',
      ],
      commitment: {
        title: 'Komitmen Kami',
        description:
          'Kami menjembatani petani kratom Indonesia dengan pasar global, memastikan kualitas, kepatuhan, dan perdagangan yang adil di setiap langkah rantai pasokan.',
        highlights: {
          quality: 'Produk Berkualitas Tinggi',
          standards: 'Standar Internasional',
          practices: 'Praktik Etis',
        },
      },
    },
    companyBackground: {
      badge: 'Fondasi Kami',
      title: 'Latar Belakang Perusahaan',
      subtitle: 'Dibangun atas prinsip integritas, kualitas, dan keberlanjutan',
      objectives: {
        enhance: {
          title: 'Meningkatkan Nilai',
          description:
            'Meningkatkan nilai produk kratom Indonesia melalui pengolahan berkualitas dan standar profesional',
        },
        bridge: {
          title: 'Menjembatani Pasar',
          description:
            'Menjembatani petani lokal dengan pasar global, menciptakan peluang ekonomi yang berkelanjutan',
        },
        promote: {
          title: 'Mempromosikan Perdagangan Legal',
          description:
            'Mempromosikan perdagangan kratom yang legal, aman, dan berkelanjutan sesuai dengan peraturan',
        },
      },
      gcg: {
        label: 'Tata Kelola Perusahaan yang Baik',
        text: 'Kami beroperasi berdasarkan prinsip Good Corporate Governance (GCG) dan menjaga perilaku bisnis yang etis dalam semua operasi kami.',
      },
    },
    visionMission: {
      badge: 'Arah Kami',
      title: 'Visi & Misi',
      vision: {
        title: 'Visi Kami',
        text: 'Menjadi perusahaan kratom Indonesia yang terpercaya, kompetitif secara global, dan berkelanjutan yang menetapkan standar kualitas dan kepatuhan dalam industri.',
      },
      mission: {
        title: 'Misi Kami',
        points: [
          'Menyediakan produk berkualitas tinggi dengan standar ekspor',
          'Membangun kemitraan strategis dengan petani lokal',
          'Memastikan kepatuhan penuh terhadap peraturan pemerintah dan hukum internasional',
          'Menjunjung transparansi, profesionalisme, dan keberlanjutan lingkungan',
        ],
      },
    },
    values: {
      badge: 'Prinsip Kami',
      title: 'Nilai-Nilai Kami',
      subtitle: 'Prinsip yang memandu setiap keputusan kami',
      items: {
        sustainability: {
          title: 'Keberlanjutan',
          description:
            'Berkomitmen pada praktik yang bertanggung jawab terhadap lingkungan dan metode panen berkelanjutan yang melindungi sumber daya alam kami.',
        },
        compliance: {
          title: 'Kepatuhan',
          description:
            'Mematuhi semua peraturan nasional dan internasional, memastikan operasi yang legal dan transparan di setiap tingkat.',
        },
        quality: {
          title: 'Kualitas',
          description:
            'Mempertahankan standar tertinggi dalam pengolahan, pengemasan, dan pengiriman untuk memastikan kualitas produk premium.',
        },
        community: {
          title: 'Komunitas',
          description:
            'Mendukung petani dan komunitas lokal melalui kemitraan yang adil dan inisiatif pemberdayaan ekonomi.',
        },
      },
    },
    products: {
      badge: 'Penawaran Kami',
      title: 'Produk Kami',
      subtitle: 'Produk kratom premium yang diproses dengan standar tertinggi',
      items: {
        driedLeaves: {
          name: 'Daun Kratom Kering',
          description:
            'Daun kering utuh premium, dipanen dan diproses dengan hati-hati untuk mempertahankan sifat alami.',
          features: [
            'Daun pilihan tangan',
            'Proses pengeringan alami',
            'Kualitas tergradasi',
          ],
        },
        powder: {
          name: 'Bubuk Kratom',
          description:
            'Bubuk kratom halus yang diproduksi dengan standar kebersihan ketat untuk kualitas yang konsisten.',
          features: [
            'Penggilingan halus',
            'Tekstur konsisten',
            'Teruji laboratorium',
          ],
        },
        crushedLeaf: {
          name: 'Daun Kratom Hancur',
          description:
            'Daun kratom yang diproses kasar, ideal untuk berbagai aplikasi dan persiapan.',
          features: [
            'Penghancuran seragam',
            'Potensi terjaga',
            'Tersedia dalam jumlah besar',
          ],
        },
      },
      processing: {
        title: 'Pengolahan & Kontrol Kualitas',
        description:
          'Standar pengolahan kami yang ketat memastikan setiap produk memenuhi persyaratan kualitas tingkat ekspor.',
        steps: [
          'Pemilihan dan penilaian bahan baku',
          'Proses pengeringan terkontrol',
          'Penggilingan dan pengolahan higienis',
          'Kontrol kualitas dan pengujian internal',
        ],
        badge: 'Kualitas Tingkat Ekspor',
      },
    },
    qualityStandards: {
      badge: 'Standar Kami',
      title: 'Standar Kualitas & Kepatuhan Regulasi',
      subtitle:
        'Memenuhi dan melampaui persyaratan kualitas dan kepatuhan internasional',
      standards: {
        sorting: {
          title: 'Penyortiran Bahan Baku',
          description:
            'Penyortiran dan penilaian bahan baku yang ketat untuk memastikan hanya kualitas premium yang masuk ke jalur pengolahan kami.',
        },
        hygiene: {
          title: 'Kontrol Kebersihan',
          description:
            'Protokol kebersihan dan sanitasi yang ketat dipertahankan di semua tahap pengolahan dan pengemasan.',
        },
        packaging: {
          title: 'Kemasan Tertelusur',
          description:
            'Sistem kemasan yang aman dan tertelusur yang memastikan integritas produk dari fasilitas ke tujuan.',
        },
        compliance: {
          title: 'Kepatuhan Ekspor',
          description:
            'Kepatuhan penuh terhadap peraturan ekspor dan persyaratan dokumentasi untuk perdagangan internasional.',
        },
      },
      statement:
        'Kami mematuhi kebijakan dan peraturan pemerintah yang berlaku mengenai kegiatan perdagangan dan ekspor kratom, memastikan kepatuhan hukum penuh dalam semua operasi kami.',
    },
    legalCompliance: {
      badge: 'Dokumentasi',
      title: 'Kepatuhan Hukum',
      subtitle: 'Izin & Dokumentasi',
      documents: {
        businessLicense: {
          title: 'Izin Usaha',
          status: 'Tersedia atas permintaan',
        },
        exportDoc: {
          title: 'Dokumentasi Ekspor',
          status: 'Tersedia atas permintaan',
        },
        qualityDoc: {
          title: 'Dokumentasi Kualitas',
          status: 'Tersedia atas permintaan',
        },
        traceability: {
          title: 'Catatan Ketertelusuran',
          status: 'Tersedia atas permintaan',
        },
      },
      note: {
        title: 'Permintaan Dokumentasi',
        text: 'Semua dokumentasi tersedia atas permintaan untuk mitra yang memenuhi syarat. Silakan hubungi kami untuk meminta akses ke dokumentasi kepatuhan dan sertifikasi kami.',
      },
    },
    partnership: {
      badge: 'Bekerja Bersama',
      title: 'Kemitraan & Keberlanjutan',
      subtitle:
        'Membangun hubungan yang langgeng sambil melindungi lingkungan kami',
      partners: {
        title: 'Kami berkolaborasi langsung dengan:',
        items: [
          'Petani kratom lokal',
          'Kelompok tani dan koperasi',
          'Mitra logistik dan ekspor',
        ],
      },
      sustainability: {
        title: 'Prinsip keberlanjutan kami meliputi:',
        items: [
          'Panen yang bertanggung jawab terhadap lingkungan',
          'Pendidikan dan bimbingan petani',
          'Mekanisme penetapan harga yang adil',
          'Pemberdayaan ekonomi lokal',
        ],
      },
    },
    market: {
      badge: 'Jangkauan Global',
      title: 'Pasar & Segmentasi',
      subtitle: 'Melayani pasar yang beragam dengan kratom premium Indonesia',
      targetMarkets: {
        title: 'Pasar Target',
        items: [
          'Distributor dan pedagang grosir',
          'Importir internasional',
          'Sektor herbal, penelitian, dan industri',
        ],
      },
      primaryMarkets: {
        title: 'Pasar Utama',
        domestic: {
          label: 'Domestik',
          description: 'Distribusi pasar Indonesia',
        },
        international: {
          label: 'Internasional',
          description: 'Operasi ekspor global',
        },
        serving: 'Melayani pelanggan di seluruh dunia dari Indonesia',
      },
    },
    contact: {
      badge: 'Hubungi Kami',
      title: 'Kontak',
      subtitle:
        'Siap mendiskusikan kebutuhan sumber kratom Anda? Hubungi kami.',
      info: {
        phone: 'Nomor Telepon',
        email: 'Alamat Email',
        website: 'Website',
        location: 'Lokasi',
      },
      since: 'Sejak 2025',
      footer: {
        tagline: 'Kualitas • Kepatuhan • Keberlanjutan',
        copyright: 'PT Kratom Borneo Indonesia. Hak cipta dilindungi.',
      },
    },
    quoteModal: {
      title: 'Minta Penawaran',
      subtitle:
        'Isi formulir di bawah ini dan kami akan menghubungi Anda dalam 24 jam.',
      fields: {
        name: {
          label: 'Nama Lengkap',
          placeholder: 'Budi Santoso',
        },
        email: {
          label: 'Alamat Email',
          placeholder: 'budi@perusahaan.com',
        },
        company: {
          label: 'Nama Perusahaan',
          placeholder: 'Perusahaan Anda PT',
        },
        message: {
          label: 'Pesan / Kebutuhan',
          placeholder:
            'Beritahu kami tentang kebutuhan, kuantitas, dan kebutuhan khusus Anda...',
        },
      },
      submit: 'Kirim Permintaan',
      submitting: 'Mengirim...',
      success: {
        title: 'Permintaan Terkirim!',
        message:
          'Terima kasih atas minat Anda. Kami akan segera menghubungi Anda.',
      },
    },
  },
}
